import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "./useAxios";
import { useAnalytics } from "./useAnalytics";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { ENVS, USER_API } from "config";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { GetProjectPaymentPageUrl } from "pages/dashboard/payment";
import { persistOtpCheckoutContext } from "pages/dashboard/payment/otpCheckoutContext";
import { PaymentStatus } from "store/types/enum";
import { handleAxiosRequest } from "utils";
import { handleToast } from "utils/toasts";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { workspaceKeys } from "queries";
import { ANALYTICS_EVENTS } from "analytics";
import { measureOpenAiAdsOrderCreated } from "lib/analytics/openai-ads-pixel";
import { toOtpCheckoutBaseline } from "utils/otpPaymentVerification";
import type { SessionUser } from "types/session-user";

export type PurchaseContext =
  | "upgrade_project"
  | "create_new_project"
  | "billing_settings"
  | "version_dropdown"
  | "project_creation";

type OneTimePurchasePayload = {
  checkout_ui?: "hosted" | "embedded";
  workspace_id?: string;
  success_url?: string;
  cancel_url?: string;
  return_url?: string;
  one_time_product_id?: number;
};

type OneTimePurchaseResponse = {
  checkout_ui: "hosted" | "embedded";
  checkout_url: string | null;
  client_secret: string | null;
  stripe_publishable_key: string | null;
};

interface UsePurchaseLicenseOptions {
  context: PurchaseContext;
  projectId?: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

const useEmbeddedCheckoutPreferred = (): boolean =>
  ENVS.otpEmbeddedCheckoutEnabled && Boolean(ENVS.stripePublishableKey?.trim());

export const usePurchaseLicense = (options: UsePurchaseLicenseOptions) => {
  const { context, projectId, onSuccess, onError } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [embeddedSession, setEmbeddedSession] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);
  const { workspaceId } = useWorkspaceIdentifiers();
  const queryClient = useQueryClient();

  const userId = useSelector((state: AppState) => state.auth.user_id);
  const axios = useAxios();
  const { trackEvent } = useAnalytics();

  const invalidateAfterPurchase = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    await queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    if (workspaceId) {
      await queryClient.invalidateQueries({ queryKey: workspaceKeys.billingSummary(workspaceId) });
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "workspaces" &&
          q.queryKey[1] === "billingInvoices" &&
          q.queryKey[2] === workspaceId,
      });
    }
  }, [queryClient, workspaceId]);

  const dismissEmbeddedCheckout = useCallback(() => {
    setEmbeddedSession(null);
  }, []);

  const handleEmbeddedComplete = useCallback(async () => {
    await invalidateAfterPurchase();
    dismissEmbeddedCheckout();
    trackEvent(ANALYTICS_EVENTS.LICENSE_PURCHASE_EMBEDDED_COMPLETED, {
      userId,
      context,
      projectId,
    });
    measureOpenAiAdsOrderCreated();
    handleToast("Purchase complete. Your licenses will update in a moment.", "success");
    onSuccess?.();
  }, [
    context,
    dismissEmbeddedCheckout,
    invalidateAfterPurchase,
    onSuccess,
    projectId,
    trackEvent,
    userId,
  ]);

  const purchaseLicense = async (overrides?: Partial<OneTimePurchasePayload>) => {
    try {
      setIsLoading(true);

      trackEvent(ANALYTICS_EVENTS.LICENSE_PURCHASE_INITIATED, {
        userId,
        context,
        projectId,
        one_time_product_id: overrides?.one_time_product_id,
      });

      const useEmbedded = useEmbeddedCheckoutPreferred();
      const payload: OneTimePurchasePayload = {
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
        ...overrides,
      };

      const session = queryClient.getQueryData<SessionUser>(sessionKeys.me());
      const checkoutContext = {
        workspaceId: workspaceId ?? undefined,
        projectId: projectId?.trim() || undefined,
        baseline: toOtpCheckoutBaseline(session?.one_time_purchases),
      };
      persistOtpCheckoutContext(checkoutContext);

      if (useEmbedded) {
        payload.checkout_ui = "embedded";
        payload.return_url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      } else {
        payload.checkout_ui = "hosted";
        payload.success_url = GetProjectPaymentPageUrl(
          PaymentStatus.CREATE_PROJECT_PURCHASE,
          true,
          checkoutContext
        );
        payload.cancel_url = window.location.href;
      }

      const response = await handleAxiosRequest<OneTimePurchaseResponse>(
        axios.post(USER_API.createProjectPurchase, payload)
      );

      if (
        response.checkout_ui === "embedded" &&
        response.client_secret &&
        response.stripe_publishable_key
      ) {
        trackEvent(ANALYTICS_EVENTS.LICENSE_PURCHASE_EMBEDDED_MOUNTED, {
          userId,
          context,
          projectId,
        });
        setEmbeddedSession({
          clientSecret: response.client_secret,
          publishableKey: response.stripe_publishable_key,
        });
        return;
      }

      if (response.checkout_url) {
        trackEvent(ANALYTICS_EVENTS.LICENSE_PURCHASE_CHECKOUT_REDIRECTED, {
          userId,
          context,
          projectId,
        });
        window.location.href = response.checkout_url;
        return;
      }
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.LICENSE_PURCHASE_FAILED, {
        userId,
        context,
        projectId,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      handleToast("Failed to initiate purchase. Please try again.", "error");
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    purchaseLicense,
    isLoading,
    embeddedSession,
    dismissEmbeddedCheckout,
    onEmbeddedPurchaseComplete: handleEmbeddedComplete,
  };
};
